"use client";

import Script from "next/script";
import { useCallback, useEffect, useRef } from "react";
import {
  buildGtmConsentUpdate,
  getGtmConsentUpdateMaxAttempts,
  parseGtmConsentWaitForUpdate,
  runGtmConsentCleanupThenUpdate,
  shouldRetryGtmConsentUpdate,
  GTM_CONSENT_UPDATE_RETRY_DELAY_MS,
} from "@/utils/analytics/gtmConsent";
import { getCookiebotConsentState } from "@/utils/analytics/cookiebot";
import {
  pushSanitizedGtmPageView,
  queueGtmContainerBootstrap,
} from "@/utils/analytics/gtmPageContext";
import {
  clearFirstPartyCookies,
  clearStoragePrefixes,
} from "@/utils/marketingVendorConsent";

const GTM_ID = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID;
const GTM_CONTAINER_SCRIPT_ID = "gtm-container-loader";

const GTM_CONSENT_WAIT_FOR_UPDATE = parseGtmConsentWaitForUpdate(
  process.env.NEXT_PUBLIC_GTM_CONSENT_WAIT_FOR_UPDATE_MS,
);
// Attempts are the retry time budget, expressed in polling cycles, for consent
// updates that arrive before GTM has created window.gtag. The retry budget is
// derived from wait_for_update so Cookiebot has the same documented window on
// both the GTM side and this client-side replay side; the native script load
// listener still replays consent once for GTM loads slower than that budget.
const GTM_CONSENT_UPDATE_MAX_ATTEMPTS = getGtmConsentUpdateMaxAttempts(
  GTM_CONSENT_WAIT_FOR_UPDATE,
);

export const GtmConsentDefaultScript = () => {
  // Keep unset-GTM environments free of a global gtag shim.
  if (!GTM_ID) {
    return null;
  }

  return (
    <Script
      id="gtm-consent-default"
      data-cookieconsent="ignore"
      strategy="beforeInteractive"
    >
      {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('set', 'ads_data_redaction', true);
        gtag('set', 'url_passthrough', true);
        gtag('consent', 'default', {
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied',
          'analytics_storage': 'denied',
          'functionality_storage': 'denied',
          'personalization_storage': 'denied',
          'security_storage': 'granted',
          'wait_for_update': ${GTM_CONSENT_WAIT_FOR_UPDATE}
        });
      `}
    </Script>
  );
};

export const GtmScript = () => {
  const isMounted = useRef(false);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearGtmConsentUpdateRetry = useCallback(() => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
  }, []);

  // This function updates GTM consent state based on Cookiebot's consent object
  const updateGtmConsent = useCallback(
    (attempt = 0) => {
      if (!GTM_ID || typeof window === "undefined" || !window.Cookiebot) {
        return;
      }

      if (!window.gtag) {
        if (!isMounted.current) {
          return;
        }

        if (
          shouldRetryGtmConsentUpdate(attempt, GTM_CONSENT_UPDATE_MAX_ATTEMPTS)
        ) {
          clearGtmConsentUpdateRetry();
          // The recursive retry uses this stable useCallback identity; the
          // mount ref prevents timers from updating consent after unmount. If
          // the retry budget ends before gtag exists, the GTM script load
          // listener below is the final backstop.
          retryTimer.current = setTimeout(() => {
            if (isMounted.current) {
              updateGtmConsent(attempt + 1);
            }
          }, GTM_CONSENT_UPDATE_RETRY_DELAY_MS);
        }
        return;
      }

      clearGtmConsentUpdateRetry();

      window.gtag(
        "consent",
        "update",
        buildGtmConsentUpdate(window.Cookiebot.consent),
      );
    },
    [clearGtmConsentUpdateRetry],
  );

  const clearRevokedConsentStorage = useCallback(() => {
    if (getCookiebotConsentState("marketing") === "denied") {
      clearFirstPartyCookies(window, document, ["_gcl_", "_fb", "_uet"]);
      try {
        clearStoragePrefixes(window.localStorage, ["_gcl_", "optitag-"]);
      } catch {
        // A blocked storage getter must not prevent the denied GTM update.
      }
    }

    if (getCookiebotConsentState("analytics") === "denied") {
      clearFirstPartyCookies(window, document, ["_ga", "_gid", "_gat", "_cl"]);
    }
  }, []);

  const loadGtmContainer = useCallback(() => {
    if (!GTM_ID || typeof document === "undefined") {
      return;
    }

    if (getCookiebotConsentState("analytics-or-marketing") !== "granted") {
      return;
    }

    if (document.getElementById(GTM_CONTAINER_SCRIPT_ID)) {
      updateGtmConsent();
      return;
    }

    queueGtmContainerBootstrap(window, updateGtmConsent);

    if (getCookiebotConsentState("analytics") === "granted") {
      pushSanitizedGtmPageView(
        window,
        `${window.location.pathname}${window.location.search}`,
      );
    }

    const firstScript = document.getElementsByTagName("script")[0];
    const gtmScript = document.createElement("script");
    gtmScript.async = true;
    gtmScript.id = GTM_CONTAINER_SCRIPT_ID;
    gtmScript.setAttribute("data-cookieconsent", "ignore");
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    gtmScript.addEventListener("load", () => updateGtmConsent(), {
      once: true,
    });

    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(gtmScript, firstScript);
      return;
    }

    document.head.appendChild(gtmScript);
  }, [updateGtmConsent]);

  useEffect(() => {
    if (!GTM_ID) return;

    isMounted.current = true;

    const handleGtmConsentUpdate = () => {
      runGtmConsentCleanupThenUpdate(
        clearRevokedConsentStorage,
        updateGtmConsent,
      );
    };
    const handleGtmConsentChanged = () => {
      runGtmConsentCleanupThenUpdate(clearRevokedConsentStorage, () => {
        try {
          loadGtmContainer();
        } finally {
          updateGtmConsent();
        }
      });
    };

    // Listen for Cookiebot consent to be ready
    window.addEventListener("CookiebotOnLoad", handleGtmConsentChanged);
    // Listen for user to accept/change consent
    window.addEventListener("CookiebotOnAccept", handleGtmConsentChanged);
    // Cookiebot updates its consent object before emitting decline/change
    // events, so re-reading consent here sends the denied update to GTM.
    window.addEventListener("CookiebotOnDecline", handleGtmConsentUpdate);
    window.addEventListener(
      "CookiebotOnConsentChanged",
      handleGtmConsentChanged,
    );

    // Backstop the script load replay from the mounted effect so development
    // Strict Mode teardown timing cannot skip the initial Cookiebot state sync.
    loadGtmContainer();
    updateGtmConsent();

    return () => {
      isMounted.current = false;
      clearGtmConsentUpdateRetry();
      window.removeEventListener("CookiebotOnLoad", handleGtmConsentChanged);
      window.removeEventListener("CookiebotOnAccept", handleGtmConsentChanged);
      window.removeEventListener("CookiebotOnDecline", handleGtmConsentUpdate);
      window.removeEventListener(
        "CookiebotOnConsentChanged",
        handleGtmConsentChanged,
      );
    };
  }, [
    clearGtmConsentUpdateRetry,
    clearRevokedConsentStorage,
    loadGtmContainer,
    updateGtmConsent,
  ]);

  return null;
};
